import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import ELPMStudioDAW from "@/components/ELPMStudioDAW";
import LandingPage from "@/components/LandingPage";
import SongViewer from "@/components/SongViewer";
import SessionViewer from "@/components/SessionViewer";
import ExplorePage from "@/components/ExplorePage";
import AdminDashboard from "@/components/AdminDashboard";
import OwnerDashboard from "@/components/OwnerDashboard";
import PackInstaller from "@/components/PackInstaller";
import SettingsPage from "@/components/SettingsPage";
import StatusPage from "@/components/StatusPage";
import AuthCallback from "@/components/AuthCallback";
import BillingSuccess from "@/components/BillingSuccess";
import PluginBrowsePage from "@/components/PluginBrowsePage";
import MarketplacePage from "@/components/marketplace/MarketplacePage";
import StemPackPage from "@/components/marketplace/StemPackPage";
import SupportBotWidget from "@/components/SupportBotWidget";
import SharedKitImporter from "@/components/SharedKitImporter";
import ErrorBoundary from "@/components/ErrorBoundary";
import ToasterBoundary from "@/components/ToasterBoundary";
import CreatorHome from "@/components/creator/CreatorHome";
import CreatorHub from "@/components/creator/CreatorHub";
import CreateSongPage from "@/components/creator/CreateSongPage";
import SongReadyPage from "@/components/creator/SongReadyPage";
import MasterPage from "@/components/creator/MasterPage";
import MyProjectsPage from "@/components/creator/MyProjectsPage";
import ExportRoute from "@/components/creator/ExportRoute";
import RecordVocalPage from "@/components/creator/RecordVocalPage";
import ProjectPage from "@/components/creator/ProjectPage";
import SingItMyselfPage from "@/components/creator/SingItMyselfPage";
import ReplaceVocalPage from "@/components/creator/replace/ReplaceVocalPage";
import StemWorkspacePage from "@/components/creator/stems/StemWorkspacePage";
import MixDeskPage from "@/components/creator/mix/MixDeskPage";
import VoiceProfilesPage from "@/components/creator/VoiceProfilesPage";
import RecStudioPage from "@/components/recstudio/RecStudioPage";
import EnhancePage from "@/components/enhance/EnhancePage";
import EnhanceSessionPage from "@/components/enhance/EnhanceSessionPage";
import MyVoicesPage from "@/components/myvoices/MyVoicesPage";
import VoiceModelPage from "@/components/myvoices/VoiceModelPage";
import SunoVoicePage from "@/components/sunovoice/SunoVoicePage";
import BackgroundSingersPage from "@/components/bgv/BackgroundSingersPage";
import BgvSessionPage from "@/components/bgv/BgvSessionPage";
import ExportAudioPage from "@/components/exportaudio/ExportAudioPage";
import MicHelpDialog from "@/components/MicHelpDialog";
import StudioGate from "@/components/creator/StudioGate";
import InfoPage from "@/components/InfoPage";
import WritingRoom from "@/components/WritingRoom";
import { useStudioMode } from "@/lib/studioMode";
import { installConsoleTail } from "@/lib/consoleTail";
import { installPlaybackUnlock } from "@/lib/playbackUnlock";
import { installErrorAutoDetect } from "@/lib/errorAutoDetect";
import { installToastGuard } from "@/lib/toastGuard";
import useAuth from "@/lib/useAuth";
import { applyThemeBg, DEFAULT_BG } from "@/lib/themeBg";
import { runThemeSelfTest } from "@/lib/themeSelfTest";

// Side-effect import — installs window.__engineHarness in dev/preview only
// for behaviour-preservation snapshot testing of the audio engine refactor.
// No-op in production (NODE_ENV check gates the global install).
import "@/lib/audioEngine/__smoke__/snapshotHarness";

// Install at module load — earliest possible point. Idempotent.
// Captures every console.error / console.warn / window.error /
// unhandledrejection for the lifetime of the tab so the /status page
// + support reports can surface them.
installConsoleTail();

// iter-95 — Arm an AudioContext + silent <audio> on the FIRST user
// gesture so subsequent .play() calls aren't rejected with
// NotAllowedError. Wraps cleanly inside the existing one-time-install
// pattern; subsequent calls are no-ops.
installPlaybackUnlock();

// iter-97 — Watch console.error + window error events for known
// patterns (audio-blocked, network failure, rate-limit) and surface a
// helpful toast with a one-click fix or a "Help" action that pre-fills
// the Support Bot. Silent for already-handled patterns (e.g. the
// pre-gesture AudioContext warning, which installPlaybackUnlock fixes).
installErrorAutoDetect();

// Sep 2026 — stringify non-renderable toast titles/descriptions (raw 422
// `detail` arrays etc.) so a bad toast can't unmount the whole React root
// (the Toaster sits outside the ErrorBoundary → "blank purple screen").
installToastGuard();

const LegacyVoiceRedirect = () => <Navigate to={`/create/my-voices/elpm/${useParams().id}`} replace />;

/**
 * Two doors. `/` opens the ELPM Creator homepage for everyone; producers who
 * flipped the Simple|Pro switch to Pro land straight in the 64-channel
 * studio on every return (remembered per browser). The marketing page
 * stays at /welcome. OAuth callbacks are detected synchronously in
 * AppRouter (hash `session_id=`) before any route renders.
 */
function RootRoute() {
  const [mode] = useStudioMode();
  return mode === "pro" ? <ELPMStudioDAW /> : <CreatorHome />;
}

/** Private studio routes — owner Google sign-in only (public share/info pages stay open). */
const gated = (el) => <StudioGate>{el}</StudioGate>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={gated(<RootRoute />)} />
      <Route path="/create" element={gated(<CreatorHub />)} />
      <Route path="/create/song" element={gated(<CreateSongPage />)} />
      <Route path="/create/song/:id" element={gated(<SongReadyPage />)} />
      <Route path="/create/project/:id" element={gated(<ProjectPage />)} />
      <Route path="/create/project/:id/sing" element={gated(<SingItMyselfPage />)} />
      <Route path="/create/project/:id/replace" element={gated(<ReplaceVocalPage />)} />
      <Route path="/create/song/:id/stems" element={gated(<StemWorkspacePage />)} />
      <Route path="/create/song/:id/mix" element={gated(<MixDeskPage />)} />
      <Route path="/create/voices" element={gated(<VoiceProfilesPage />)} />
      <Route path="/create/record" element={gated(<RecordVocalPage />)} />
      <Route path="/create/record/:id" element={gated(<RecordVocalPage />)} />
      <Route path="/create/studio" element={gated(<RecStudioPage />)} />
      <Route path="/create/studio/:sessionId" element={gated(<RecStudioPage />)} />
      <Route path="/create/enhance" element={gated(<EnhancePage />)} />
      <Route path="/create/enhance/:id" element={gated(<EnhanceSessionPage />)} />
      <Route path="/create/my-voices" element={gated(<MyVoicesPage />)} />
      <Route path="/create/my-voices/:id" element={gated(<VoiceModelPage />)} />
      <Route path="/create/my-voices/elpm/:id" element={gated(<SunoVoicePage />)} />
      <Route path="/create/my-voices/suno/:id" element={<LegacyVoiceRedirect />} />
      <Route path="/create/background-singers" element={gated(<BackgroundSingersPage />)} />
      <Route path="/create/background-singers/:id" element={gated(<BgvSessionPage />)} />
      <Route path="/create/export-audio" element={gated(<ExportAudioPage />)} />
      <Route path="/create/master" element={gated(<MasterPage />)} />
      <Route path="/create/master/:id" element={gated(<MasterPage />)} />
      <Route path="/create/projects" element={gated(<MyProjectsPage />)} />
      <Route path="/create/export" element={gated(<ExportRoute />)} />
      <Route path="/about" element={<InfoPage />} />
      <Route path="/write/:roomId" element={<WritingRoom />} />
      <Route path="/studio" element={gated(<ELPMStudioDAW />)} />
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/dashboard" element={gated(<ELPMStudioDAW />)} />
      <Route path="/song/:id" element={<SongViewer />} />
      <Route path="/session/:id" element={<SessionViewer />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/owner" element={<OwnerDashboard />} />
      <Route path="/pack/:id" element={<PackInstaller />} />
      <Route path="/settings" element={gated(<SettingsPage />)} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/plugins" element={<PluginBrowsePage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/pack/:packId" element={<StemPackPage />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingSuccess />} />
    </Routes>
  );
}

import { useEffect } from "react";

/** Applies the signed-in user's saved background theme as soon as it lands.
 *  Anonymous visitors keep the default `--app-bg`. */
function ThemeBootstrap() {
  const { user } = useAuth();
  useEffect(() => {
    const slug = user?.preferences?.theme_bg || DEFAULT_BG;
    applyThemeBg(slug);
    // iter-101 — Run the once-per-session self-test. If the browser
    // silently rejected the CSS (the iter-97 regression class), this
    // reverts to default + files a support ticket so the owner sees it.
    runThemeSelfTest(slug);
  }, [user]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <ThemeBootstrap />
        <SharedKitImporter />
        {/* iter-153 — ErrorBoundary contains React render/effect throws so a
            single bad component doesn't unmount the whole app to a blank
            page. Wraps the routes + support widget; ThemeBootstrap /
            SharedKitImporter / Toaster are intentionally outside so the
            recovery UI still has working toasts + theme. */}
        <ErrorBoundary>
          <AppRouter />
          <SupportBotWidget />
          <MicHelpDialog />
        </ErrorBoundary>
        <ToasterBoundary />
      </div>
    </BrowserRouter>
  );
}

export default App;
